import { resolveAgainstArena, type Circle } from './collision.js';
import type { Vec2 } from '../types.js';

export interface Locomotor {
  x: number;
  y: number;
  vx: number;
  vy: number;
  radius: number;
  speed: number;
  path: Vec2[] | null;
  pathIndex: number;
}

const WAYPOINT_REACH = 6;

/**
 * Advance one entity by `dt` seconds. Keyboard input (`moveX`/`moveY`) takes
 * priority and cancels any active path. With no keyboard input the entity
 * follows its path (click-to-move). No inertia: velocity is set directly so
 * local input feels instant and prediction stays simple.
 *
 * Pure and deterministic: the client runs this for local prediction and the
 * server runs the exact same code authoritatively.
 */
export function stepLocomotion(m: Locomotor, moveX: number, moveY: number, dt: number): boolean {
  let hasKeyboard = moveX !== 0 || moveY !== 0;

  if (hasKeyboard) {
    m.path = null;
    m.pathIndex = 0;
    const len = Math.hypot(moveX, moveY) || 1;
    m.vx = (moveX / len) * m.speed;
    m.vy = (moveY / len) * m.speed;
  } else if (m.path && m.pathIndex < m.path.length) {
    const wp = m.path[m.pathIndex]!;
    const dx = wp.x - m.x;
    const dy = wp.y - m.y;
    const dist = Math.hypot(dx, dy);
    if (dist <= WAYPOINT_REACH) {
      m.pathIndex++;
      if (m.pathIndex >= m.path.length) {
        m.path = null;
        m.pathIndex = 0;
        m.vx = 0;
        m.vy = 0;
      }
    } else {
      const step = Math.min(1, (m.speed * dt) / dist);
      m.vx = dx * step * (1 / dt);
      m.vy = dy * step * (1 / dt);
    }
  } else {
    m.vx = 0;
    m.vy = 0;
  }

  m.x += m.vx * dt;
  m.y += m.vy * dt;

  const circle: Circle = { x: m.x, y: m.y, r: m.radius };
  if (resolveAgainstArena(circle)) {
    m.x = circle.x;
    m.y = circle.y;
    // Blocked: if we were pathing, keep the path so the caller can repath.
  } else {
    m.x = circle.x;
    m.y = circle.y;
  }

  const moving = Math.abs(m.vx) > 1 || Math.abs(m.vy) > 1;
  hasKeyboard = hasKeyboard && moving;
  return moving;
}

export function angleOf(vx: number, vy: number, fallback: number): number {
  if (Math.abs(vx) < 0.001 && Math.abs(vy) < 0.001) return fallback;
  return Math.atan2(vy, vx);
}
